/**
 * Agent functions list page — `GET /agent-functions`.
 *
 * Migrated onto the CoOrdio list-page pattern (see `ProjectListPage.tsx`, the reference):
 * `ShellHeader` + `CordioAbpPagedDataTable` + one `DeleteDialog`, with the grid owning paging,
 * search and filtering. The page's `filters` / `totalCount` state and the `loadData` callback that
 * refetched on every change are gone; `listVersion` is the single piece of refetch state left.
 *
 * ─── Column plan ────────────────────────────────────────────────────────────────────────────────
 * field                │ header   │ width │ cell type        │ filter
 * ─────────────────────┼──────────┼───────┼──────────────────┼──────────────────────────────────────
 * name                 │ Name     │  22%  │ text (clickable) │ toolbar search (`searchTerm`)
 * slug                 │ Slug     │  20%  │ text             │ —
 * agentDefinitionName  │ Agent    │  20%  │ text             │ —
 * requestCount         │ Requests │  10%  │ number           │ —
 * createdAt            │ Created  │  16%  │ date (locale)    │ —
 * isActive             │ Status   │  12%  │ custom Chip      │ status tabs → synthetic `status`
 *
 * The old table showed the same six fields, so nothing was dropped; `version` was never a column
 * and still is not.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 *
 * NO FILTER POPOVER, DELIBERATELY: `agentFunctionService.getAgentFunctions` accepts exactly four
 * parameters — `pageNumber`, `pageSize`, `searchTerm`, `isActive`. The first two belong to the
 * grid, the third is the toolbar's search box and the fourth is the status tabs, so there is
 * nothing left that the endpoint would honour. Declaring a filter it silently drops (a `name` or
 * `agentDefinitionId` box, say) would give the user a control that does nothing, so the toolbar
 * ships without one and `cordio-datatable-filter-trigger` is absent on this page by design.
 *
 * No column is `sortable` for the same reason: the endpoint has nowhere to put `sortBy` /
 * `sortDescending`, so a sort header would silently do nothing.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  CordioAbpPagedDataTable,
  Chip,
  ShellHeader,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { Plus, Trash2 } from 'lucide-react';

import { DeleteDialog, useDeleteDialog } from '../components/delete-dialog';
import { createCortexPagedDataSource } from '../components/data-display/cortexPagedDataSource';
import { agentFunctionService } from '../services/agentFunctionService';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';

import type { AgentFunctionDto } from '../types/agentFunction';

import './AgentFunctionsListPage.scss';

/**
 * Quick-filter tabs over the function's `isActive` dimension.
 *
 * The All tab declares the literal `'all'` because the grid builds each tab's test id as
 * `cordio-datatable-status-tab-<value>`; an empty value emits `cordio-datatable-status-tab-` and
 * GRID-13's `…-status-tab-all` selector matches nothing.
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
] as const;

/**
 * Synthetic toolbar key the status tabs write to. Never sent to the API as-is — `'all'` is a UI
 * concept the endpoint has no value for, so {@link toAgentFunctionQuery} maps it to `isActive` and
 * drops it entirely for All.
 */
const STATUS_TAB_PARAM_KEY = 'status';

/** Turns the grid's query into the one `agentFunctionService.getAgentFunctions` accepts. */
function toAgentFunctionQuery(query: Record<string, unknown>): Record<string, unknown> {
  const { [STATUS_TAB_PARAM_KEY]: status, ...rest } = query;

  if (status === 'true' || status === 'false') {
    return { ...rest, isActive: status === 'true' };
  }
  return rest;
}

export const AgentFunctionsListPage = () => {
  const navigate = useNavigate();

  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);
  const { showNotification } = useNotificationStore();

  /** Bumped after a mutation to rebuild `dataSource` and make the grid refetch. */
  const [listVersion, setListVersion] = useState(0);

  const { dialogRef, pendingItem, openDelete, closeDelete } = useDeleteDialog<AgentFunctionDto>();

  const tenantId = currentTenant?.id;
  const projectId = currentProject?.id;

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<AgentFunctionDto>((query) =>
        agentFunctionService.getAgentFunctions(toAgentFunctionQuery(query)),
      ),
    // `tenantId` / `projectId` scope the request through the API client's headers rather than the
    // query string, so they still have to invalidate the list. `listVersion` is the deliberate
    // refetch lever: changing the identity of `dataSource` is the documented way to reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantId, projectId, listVersion],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<AgentFunctionDto>>>(
    () => [
      { field: 'name', header: 'Name', type: 'text', width: '22%', clickable: true },
      // The slug is the path segment callers POST to, so it is the identifier that shows up in
      // integration code and logs.
      { field: 'slug', header: 'Slug', type: 'text', width: '20%' },
      { field: 'agentDefinitionName', header: 'Agent', type: 'text', width: '20%' },
      { field: 'requestCount', header: 'Requests', type: 'number', width: '10%' },
      { field: 'createdAt', header: 'Created', type: 'date', dateFormat: 'locale', width: '16%' },
      {
        field: 'isActive',
        header: 'Status',
        type: 'custom',
        width: '12%',
        customCell: (row) => (
          <Chip label={row.isActive ? 'Active' : 'Inactive'} color={row.isActive ? 'green' : 'default'} />
        ),
      },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search functions by name' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'Agent function status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
      // No `filters`: see the file header — the endpoint has no filter dimension left to expose.
      filterButtonLabel: 'Filter',
    }),
    [],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<AgentFunctionDto>>>(
    () => [
      {
        icon: 'eye',
        tooltip: 'View function',
        onClick: (row) => navigate(ROUTES.AGENT_FUNCTION_DETAIL.replace(':id', row.id)),
      },
      {
        icon: 'play',
        tooltip: 'Execute function',
        // The old page rendered a disabled button for inactive functions; hiding it says the same
        // thing without offering a control that cannot be used.
        visible: (row) => row.isActive,
        onClick: (row) => navigate(`${ROUTES.AGENT_FUNCTION_EXECUTE}?slug=${encodeURIComponent(row.slug)}`),
      },
      {
        icon: 'square-pen',
        tooltip: 'Edit function',
        onClick: (row) => navigate(ROUTES.AGENT_FUNCTION_EDIT.replace(':id', row.id)),
      },
      {
        icon: 'history',
        tooltip: 'Execution history',
        onClick: (row) => navigate(ROUTES.AGENT_FUNCTION_REQUESTS.replace(':id', row.id)),
      },
      {
        icon: 'trash-2',
        tooltip: 'Delete function',
        className: 'agent-functions__action--destructive',
        onClick: (row) => openDelete(row),
      },
    ],
    [navigate, openDelete],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingItem) return;

    await agentFunctionService.deleteAgentFunction(pendingItem.id);
    showNotification('Agent function deleted successfully', 'success');
    setListVersion((v) => v + 1);
  }, [pendingItem, showNotification]);

  return (
    <div className="agent-functions" data-testid="agent-function-list-page">
      <ShellHeader
        title="Agent Functions"
        description="Callable endpoints backed by an agent — each one takes a JSON input, runs a prompt and returns a JSON result."
        data-testid="agent-function-list-header"
        actions={
          <Button
            type="button"
            variant="default"
            size="sm"
            className="gap-2"
            data-testid="add-agent-function-btn"
            onClick={() => navigate(ROUTES.AGENT_FUNCTION_NEW)}
          >
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            Create Agent Function
          </Button>
        }
      />

      <div data-testid="agent-function-list-grid">
        <CordioAbpPagedDataTable<AgentFunctionDto>
          className="agent-functions__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={(row) => navigate(ROUTES.AGENT_FUNCTION_DETAIL.replace(':id', row.id))}
        />
      </div>

      <DeleteDialog
        ref={dialogRef}
        title="Delete agent function?"
        entityLabel={pendingItem?.name}
        dialogIcon={<Trash2 className="delete-dialog__icon" />}
        description="Callers of this slug start failing immediately, and the function's execution history is removed with it. This cannot be undone."
        confirmLabel="Delete"
        confirmVariant="destructive"
        busyLabel="Deleting…"
        onConfirm={handleDeleteConfirm}
        onCancel={closeDelete}
      />
    </div>
  );
};
